/**
 * Initialize Mailbox Page - Microsoft 365 Style
 * Bulk initialize internal memo mailboxes for users
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  makeStyles,
  Text,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ProgressBar,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  MailRegular,
  PersonRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { TablePagination, SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterDropdown: {
    minWidth: '180px',
  },
  searchInput: {
    minWidth: '250px',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e1dfdd',
  },
  selectAllContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thCheckbox: {
    width: '48px',
    padding: '12px 16px',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  trSelected: {
    backgroundColor: '#f0f6ff',
    ':hover': {
      backgroundColor: '#e5effa',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  userList: {
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  userListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
  },
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px 0',
  },
  progressStats: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
  },
  progressStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  resultSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  resultSummary: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    padding: '16px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    marginBottom: '4px',
  },
  resultSuccess: {
    borderLeft: `3px solid ${tokens.colorPaletteGreenBorder2}`,
  },
  resultFailed: {
    borderLeft: `3px solid ${tokens.colorPaletteRedBorder2}`,
  },
  resultsList: {
    maxHeight: '250px',
    overflowY: 'auto',
  },
});

interface UserWithoutMailbox {
  user_id: number;
  employee_id: number;
  full_name: string;
  email: string;
  department_id: number;
  department_name: string;
  role_id: number;
  role_name: string;
}

interface Department {
  id: number;
  department_name: string;
}

interface Role {
  id: number;
  name: string;
}

interface JobResult {
  user_id: number;
  employee_id: number;
  full_name: string;
  status: 'success' | 'failed';
  mailbox_id?: number;
  friendly_alias?: string;
  error_message?: string;
}

interface JobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_users: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
  progress_percent: number;
  results?: JobResult[];
}

export const InitializeMailboxFluentUI: React.FC = () => {
  const styles = useStyles();

  // Data state
  const [users, setUsers] = useState<UserWithoutMailbox[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  // Filter state
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);

  // Job state
  const [_activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);

  // Fetch users without mailbox
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (departmentFilter !== 'all') {
        params.department_id = departmentFilter;
      }
      if (roleFilter !== 'all') {
        params.role_id = roleFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await ApiClient.get('/api/admin/users-without-mailbox', { params });
      if (response.data.status === 'success') {
        setUsers(response.data.data.users || []);
        setTotalItems(response.data.data.pagination?.total || 0);
      } else {
        newToast('error', response.data.message || 'Failed to fetch users');
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, departmentFilter, roleFilter, searchQuery]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [deptRes, roleRes] = await Promise.all([
        ApiClient.get('/get-departments'),
        ApiClient.get('/get-roles'),
      ]);

      if (deptRes.data.status === 'success') {
        setDepartments(deptRes.data.data || []);
      }
      if (roleRes.data.status === 'success') {
        setRoles(roleRes.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [departmentFilter, roleFilter, searchQuery]);

  // Handle checkbox selection
  const handleSelectUser = (userId: number, checked: boolean) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUserIds(newSelected);
  };

  // Handle select all on current page
  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      users.forEach(user => newSelected.add(user.user_id));
    } else {
      users.forEach(user => newSelected.delete(user.user_id));
    }
    setSelectedUserIds(newSelected);
  };

  // Check if all users on current page are selected
  const allSelected = users.length > 0 && users.every(user => selectedUserIds.has(user.user_id));
  const someSelected = users.some(user => selectedUserIds.has(user.user_id)) && !allSelected;

  // Get selected users data
  const getSelectedUsers = (): UserWithoutMailbox[] => {
    return users.filter(user => selectedUserIds.has(user.user_id));
  };

  // Start initialization
  const handleInitialize = async () => {
    setShowConfirmDialog(false);
    setShowProgressDialog(true);

    try {
      const response = await ApiClient.post(
        '/api/admin/initialize-mailboxes',
        { user_ids: Array.from(selectedUserIds) },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status === 'success') {
        setActiveJobId(response.data.data.job_id);
        pollJobStatus(response.data.data.job_id);
      } else {
        setShowProgressDialog(false);
        newToast('error', response.data.message || 'Failed to start initialization');
      }
    } catch (error) {
      setShowProgressDialog(false);
      newToast('error', getErrorMessage(error));
    }
  };

  // Poll job status
  const pollJobStatus = async (jobId: string) => {
    try {
      const response = await ApiClient.get(`/api/admin/mailbox-job-status/${jobId}`);
      if (response.data.status === 'success') {
        const status = response.data.data as JobStatus;
        setJobStatus(status);

        if (status.status === 'completed' || status.status === 'failed') {
          setShowProgressDialog(false);
          setShowResultsDialog(true);
          setSelectedUserIds(new Set());
          fetchUsers();
        } else {
          setTimeout(() => pollJobStatus(jobId), 2000);
        }
      }
    } catch (error) {
      console.error('Failed to poll job status:', error);
      setTimeout(() => pollJobStatus(jobId), 2000);
    }
  };

  // Close results and reset
  const handleCloseResults = () => {
    setShowResultsDialog(false);
    setJobStatus(null);
    setActiveJobId(null);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Initialize Mailboxes</Text>
        <Text className={styles.pageSubtitle}>
          Create internal memo mailboxes for users who don't have one
        </Text>
      </div>

      {/* Filters */}
      <div className={styles.filterRow}>
        <div className={styles.filterDropdown}>
          <SearchableDropdown
            placeholder="All Departments"
            selectedValue={departmentFilter}
            onSelect={value => setDepartmentFilter(value)}
            options={[
              { id: 'all', label: 'All Departments' },
              ...departments.map(d => ({
                id: d.id.toString(),
                label: d.department_name,
              })),
            ]}
          />
        </div>
        <div className={styles.filterDropdown}>
          <SearchableDropdown
            placeholder="All Roles"
            selectedValue={roleFilter}
            onSelect={value => setRoleFilter(value)}
            options={[
              { id: 'all', label: 'All Roles' },
              ...roles.map(r => ({
                id: r.id.toString(),
                label: r.name,
              })),
            ]}
          />
        </div>
        <div className={styles.searchInput}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e1dfdd',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </div>
      </div>

      {/* Users Table */}
      <ContentCard noPadding>
        {/* Action Bar */}
        <div className={styles.actionBar}>
          <div className={styles.selectAllContainer}>
            <Checkbox
              checked={allSelected ? true : someSelected ? 'mixed' : false}
              onChange={(_, data) => handleSelectAll(!!data.checked)}
              disabled={users.length === 0}
            />
            <Text>
              {selectedUserIds.size > 0
                ? `${selectedUserIds.size} user(s) selected`
                : `Select users (${totalItems} without mailbox)`}
            </Text>
          </div>
          <Button
            appearance="primary"
            icon={<MailRegular />}
            disabled={selectedUserIds.size === 0}
            onClick={() => setShowConfirmDialog(true)}
          >
            Initialize Selected ({selectedUserIds.size})
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className={styles.emptyState}>
            <Spinner size="medium" label="Loading users..." />
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No users without mailboxes
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              All users have been assigned mailboxes, or no users match the current filters
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.thCheckbox}></th>
                <th className={styles.th}>User</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Department</th>
                <th className={styles.th}>Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr
                  key={user.user_id}
                  className={`${styles.tr} ${selectedUserIds.has(user.user_id) ? styles.trSelected : ''}`}
                >
                  <td className={styles.td}>
                    <Checkbox
                      checked={selectedUserIds.has(user.user_id)}
                      onChange={(_, data) => handleSelectUser(user.user_id, !!data.checked)}
                    />
                  </td>
                  <td className={styles.td}>
                    <div className={styles.userCell}>
                      <Avatar name={user.full_name} color="brand" size={32} />
                      <Text weight="semibold">{user.full_name}</Text>
                    </div>
                  </td>
                  <td className={styles.td}>{user.email || '—'}</td>
                  <td className={styles.td}>
                    <Badge appearance="outline">{user.department_name || '—'}</Badge>
                  </td>
                  <td className={styles.td}>{user.role_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={(_, data) => setShowConfirmDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Initialize Mailboxes</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Text>
                You are about to initialize mailboxes for{' '}
                <strong>{selectedUserIds.size} user(s)</strong>. This will enable them to send and
                receive internal memos.
              </Text>
              <div className={styles.userList}>
                {getSelectedUsers()
                  .slice(0, 10)
                  .map(user => (
                    <div key={user.user_id} className={styles.userListItem}>
                      <Avatar name={user.full_name} size={24} />
                      <Text size={200}>
                        {user.full_name} ({user.department_name})
                      </Text>
                    </div>
                  ))}
                {selectedUserIds.size > 10 && (
                  <Text size={200} style={{ color: '#605e5c', marginTop: '8px' }}>
                    ... and {selectedUserIds.size - 10} more
                  </Text>
                )}
              </div>
              <Text size={200} style={{ color: '#605e5c' }}>
                This action will be logged for audit purposes.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button appearance="primary" icon={<MailRegular />} onClick={handleInitialize}>
                Initialize Now
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Progress Dialog */}
      <Dialog open={showProgressDialog} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              <ArrowSyncRegular style={{ marginRight: '8px' }} />
              Initializing Mailboxes...
            </DialogTitle>
            <DialogContent>
              <div className={styles.progressSection}>
                <ProgressBar
                  value={jobStatus?.progress_percent ? jobStatus.progress_percent / 100 : undefined}
                  thickness="large"
                />
                <Text style={{ textAlign: 'center' }}>
                  Processing: {jobStatus?.processed_count || 0} of{' '}
                  {jobStatus?.total_users || selectedUserIds.size} users
                </Text>
                <div className={styles.progressStats}>
                  <div className={styles.progressStat}>
                    <CheckmarkCircleRegular
                      style={{ color: tokens.colorPaletteGreenForeground1 }}
                    />
                    <Text>Success: {jobStatus?.success_count || 0}</Text>
                  </div>
                  <div className={styles.progressStat}>
                    <DismissCircleRegular style={{ color: tokens.colorPaletteRedForeground1 }} />
                    <Text>Failed: {jobStatus?.failed_count || 0}</Text>
                  </div>
                </div>
                <Text size={200} style={{ textAlign: 'center', color: '#605e5c' }}>
                  Please wait while mailboxes are being created. Do not close this window.
                </Text>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={showResultsDialog}
        onOpenChange={(_, data) => data.open || handleCloseResults()}
      >
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>
              <CheckmarkCircleRegular
                style={{ marginRight: '8px', color: tokens.colorPaletteGreenForeground1 }}
              />
              Mailbox Initialization Complete
            </DialogTitle>
            <DialogContent>
              <div className={styles.resultSection}>
                <div className={styles.resultSummary}>
                  <div className={styles.progressStat}>
                    <CheckmarkCircleRegular
                      style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '24px' }}
                    />
                    <Text weight="semibold" size={500}>
                      {jobStatus?.success_count || 0} Successful
                    </Text>
                  </div>
                  <div className={styles.progressStat}>
                    <DismissCircleRegular
                      style={{ color: tokens.colorPaletteRedForeground1, fontSize: '24px' }}
                    />
                    <Text weight="semibold" size={500}>
                      {jobStatus?.failed_count || 0} Failed
                    </Text>
                  </div>
                </div>

                {jobStatus?.results && jobStatus.results.length > 0 && (
                  <div className={styles.resultsList}>
                    {jobStatus.results.map(result => (
                      <div
                        key={result.user_id}
                        className={`${styles.resultItem} ${result.status === 'success' ? styles.resultSuccess : styles.resultFailed}`}
                      >
                        {result.status === 'success' ? (
                          <CheckmarkCircleRegular
                            style={{ color: tokens.colorPaletteGreenForeground1 }}
                          />
                        ) : (
                          <DismissCircleRegular
                            style={{ color: tokens.colorPaletteRedForeground1 }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <Text weight="semibold" size={200}>
                            {result.full_name}
                          </Text>
                          {result.status === 'success' ? (
                            <Text size={200} style={{ color: '#605e5c', display: 'block' }}>
                              → {result.friendly_alias}
                            </Text>
                          ) : (
                            <Text
                              size={200}
                              style={{ color: tokens.colorPaletteRedForeground1, display: 'block' }}
                            >
                              {result.error_message}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleCloseResults}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default InitializeMailboxFluentUI;
